import * as moment from 'moment-timezone';
import Swal from 'sweetalert2';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DatatableComponent } from '@swimlane/ngx-datatable';
import { firstValueFrom, forkJoin, Subscription } from 'rxjs';
import { GasprecioService } from '../../../services/gasprecio.service';
import { FuelOrdersService } from '../../../services/fuel-orders.service';
import { SharedService } from '../../../services/shared.service';

/**
 * Vista ADMIN (MOBICO) de las solicitudes de pipas: una sola tabla, UNA fila por
 * pedido y SIN desglose de historial (decisión del equipo: manejarlo simple). El
 * Tipo y el estatus de cada fila reflejan la solicitud en curso: si el pedido tiene
 * un cambio en vuelo, la fila muestra ese cambio (y Aprobar/Rechazar responden ese
 * cambio); si no, muestra el pedido tal cual. La columna Tipo conserva los colores
 * de las secciones del formato oficial MOBICO. Al aprobar un cambio, el backend lo
 * aplica al pedido original. La ve el usuario admin de MOBICO al entrar a
 * "Solicitud de Pipas" (ver isMobicoPipesAdmin en fuel-orders.service).
 */
@Component({
  selector: 'app-pipes-admin',
  templateUrl: './pipes-admin.component.html',
  styleUrls: ['./pipes-admin.component.scss']
})
export class PipesAdminComponent implements OnInit, OnDestroy {

  // Modo estación (página /gasprecio/solicitud-pipas del admin): el panel queda
  // segmentado a la estación seleccionada — el filtro de estación se fija al
  // idStation del localStorage (mismo que usa la vista del solicitante) y se
  // ocultan los filtros de grupo/zona/estación (solo queda la semana).
  @Input() onlyStation = false;

  // La carga inicial se difiere a ngOnInit (los BehaviorSubject de SharedService
  // emiten dentro del constructor). Mismo patrón que PipesComponent.
  private inited = false;

  // Instancia de la ngx-datatable de Solicitudes (vive tras *ngIf="reqst === 1").
  // Se usa para recalculate(): ver scheduleTableRecalc.
  @ViewChild(DatatableComponent) private requestsTable?: DatatableComponent;

  endDate;
  idGroup = '60afa99655f4ee369d1df409';
  idGroupSub$!: Subscription;
  idZone = '';
  loading = false;
  reqst = 0;
  rol;
  startDate;
  weekSelected: any;
  weekSelected$!: Subscription;
  zone$!: Subscription;

  // Aviso único de solicitudes NUEVAS por revisar (ids ya avisados en localStorage);
  // se notifica con un toast (no bloqueante, se cierra solo), el detalle vive en la tabla.
  private readonly noticeStorageKey = 'pipesAdminNotifiedRequests';

  // Confirmación Aprobar/Rechazar: modal op-confirm de la familia del flujo (header
  // de color según la acción, tarjeta con el detalle y error inline).
  confirmAction: { row: any; status: 'confirmed' | 'rejected'; title: string; actionLabel: string; successTitle: string; needsDeliveryDate: boolean } | null = null;
  respondingOrder = false;
  confirmError = '';
  // Fecha de entrega confirmada: la captura el admin al aprobar un pedido primario
  // (los cambios no la piden). Se precarga con la fecha requerida por la estación.
  confirmDeliveryDate = '';
  // Entrega/costos capturados al aprobar (opcionales; el PATCH los acepta). Litros
  // ACEPTADOS por Mobico (pueden diferir de los solicitados), costo total y costo
  // por litro. Se precargan al abrir el modal de aprobación.
  confirmLiters: number | null = null;
  confirmTotal: number | null = null;
  confirmSupplyPrice: number | null = null;
  // Cuál de los dos costos escribió el admin por última vez: define cuál se
  // recalcula cuando cambian los litros (el otro se deriva de T = L × P).
  private lastCostEdited: 'supply' | 'total' | null = null;

  // Filtro por estación (buscable). '' = todas las estaciones del grupo/zona.
  stationFilter = '';
  stationOptions: { label: string; value: string }[] = [];
  showStations = false;

  // Tabla única "Solicitudes": una fila por pedido. Al expandir un pedido, sus filas
  // de historial (cada cambio) se insertan debajo ocupando las mismas columnas. El
  // Tipo y el estatus de la fila padre reflejan la solicitud en curso: el cambio en
  // vuelo si lo hay, si no el pedido.
  requests: any[] = [];
  // Pedidos con su historial expandido (por _id de pedido): controla el dropdown de
  // filas hijas. Se guardan los datos crudos para reconstruir sin recargar.
  expandedHistory = new Set<string>();
  // Historiales en cierre animado: las filas siguen en la tabla con `_closing`
  // hasta que termina la animación de salida y se retiran del array.
  closingHistory = new Set<string>();
  // Identidad de fila para el bookkeeping interno de ngx-datatable (selección,
  // índices). OJO: el trackBy real del *ngFor es [trackByProp]="'_id'" en el
  // template — rowTrackingFn ignora rowIdentity y sin trackByProp trackea por
  // índice posicional (lo que re-animaba filas al insertar/quitar historial).
  rowIdentity = (row: any) => String(row?._id ?? '');
  // Render incremental (pedido 2026-07-13 "sin paginación, tipo scroll infinito"):
  // todos los pedidos viven bajo el scroll interno de la tabla y se PINTAN por
  // lotes conforme el scroll se acerca al fondo. Los datos ya están completos en
  // memoria (getData trae todo); el corte es solo de render, para no pagar el DOM
  // de una semana muy cargada de golpe — estas tablas van con scrollbarV=false a
  // propósito (las animaciones del historial necesitan filas en flujo normal).
  // El corte es por PEDIDOS: el historial expandido no cuenta y nunca se separa
  // de su pedido.
  private readonly renderBatch = 30;
  private renderLimit = 30;
  // La tabla conserva su altura fija de 15 renglones (530px con header): con
  // menos filas se rellena con placeholders "—" (ver rebuildRequests).
  private readonly minRows = 15;
  // Pedidos completos (para avisos y conteos, independientes del corte de render).
  allParents: any[] = [];
  private orderRows: any[] = [];
  private inFlightByOrder = new Map<string, any>();
  changesRows: any[] = [];
  // Empty state de la tabla: misma línea visual que el recuadro "Solicita una pipa"
  // del flujo (ícono tenue + título + texto guía). ngx-datatable usa innerHTML.
  // Getter: el texto guía se ajusta al modo estación (segmentado a una estación).
  get emptyRequestsMessage(): string {
    const hint = this.onlyStation
      ? 'Las solicitudes nuevas de esta estación aparecerán aquí.'
      : 'Las solicitudes nuevas de las estaciones aparecerán aquí.';
    return `
    <div class="pipes-empty">
      <i class="fa fa-inbox" aria-hidden="true"></i>
      <span class="pipes-empty__title">Sin solicitudes esta semana</span>
      <span class="pipes-empty__text">${hint}</span>
    </div>`;
  }

  // Nombres comerciales MOBICO (Extra/Supreme/Diésel) → interno GASPRE (regular/premium/diesel).
  readonly productOptions: { value: string; label: string }[] = [
    { value: 'regular', label: 'Extra' },
    { value: 'premium', label: 'Supreme' },
    { value: 'diesel', label: 'Diésel' },
  ];
  // Tipos de solicitud del formato MOBICO (etiqueta de la columna Tipo).
  readonly changeTypeLabels: { [key: string]: string } = {
    addition: 'Adición',
    cancel: 'Cancelación',
    date: 'Fecha',
    capacity: 'Capacidad',
    product: 'Producto',
  };
  // La columna "Respuesta MOBICO" muestra el estatus (ya no hay texto libre).
  readonly changeStatusLabels: { [key: string]: string } = {
    pending: 'Pendiente',
    review: 'En revisión',
    confirmed: 'Confirmada',
    rejected: 'Rechazada',
    cancelled: 'Cancelada',
  };

  constructor(
    public gasprecioService: GasprecioService,
    private sharedService: SharedService,
    private fuelOrders: FuelOrdersService
  ) {
    this.rol = localStorage.getItem('rol');
    this.idGroup = (this.rol === 'Dios') ? localStorage.getItem('group') || '60afa99655f4ee369d1df409' : localStorage.getItem('group');

    this.zone$ = sharedService.sharedZone.subscribe((zone: any) => {
      if (zone !== this.idZone) {
        // En modo estación la zona y el filtro de estación son fijos: se ignoran
        // los cambios de zona compartida (pueden venir arrastrados del tablero).
        if (this.onlyStation) { return; }
        this.idZone = zone;
        // El filtro de estación depende de la zona: al cambiarla se limpia la
        // selección y se recargan las opciones (solo estaciones de esa zona),
        // para no cruzar una estación de otra zona con la zona activa.
        this.stationFilter = '';
        if (this.inited) { this.getStationsGroup(); }
        if (!this.loading && this.inited) { this.getData(); }
      }
    });

    this.weekSelected$ = this.sharedService.sharedWeekSelected.subscribe(weekSelected => {
      if (weekSelected !== this.weekSelected) {
        this.startDate = weekSelected['start_date'];
        this.endDate = weekSelected['end_date'];
        if (!this.loading && this.inited) { this.getData(); }
      }
    });

    this.idGroupSub$ = this.sharedService.sharedGroup.subscribe(idGroup => {
      if (idGroup !== this.idGroup && idGroup !== '') {
        this.idGroup = idGroup;
        this.idZone = '';
        // En modo estación el filtro fijo a la estación seleccionada no se toca.
        if (!this.onlyStation) {
          this.stationFilter = '';
          if (this.inited) { this.getStationsGroup(); }
        }
        if (!this.loading && this.inited) { this.getData(); }
      }
    });
  }

  ngOnInit(): void {
    this.inited = true;
    if (this.onlyStation) {
      // Segmentación por estación: mismo origen que la vista del solicitante.
      // Sin estación en storage se degrada a "todas" (comportamiento previo).
      this.stationFilter = localStorage.getItem('idStation') || '';
      this.idZone = '';
      this.getData();
      return;
    }
    this.getStationsGroup();
    this.getData();
  }

  ngOnDestroy(): void {
    this.idGroupSub$.unsubscribe();
    this.weekSelected$.unsubscribe();
    this.zone$.unsubscribe();
  }

  // La altura de la tabla vive en CSS (.orders-summary-scroll): min-height de
  // 3 filas, crece con el contenido y max-height de 15 filas con scroll interno.
  // Sin cálculos en px que se desfasen del render real.

  /** Nombre comercial MOBICO del producto interno (fallback cuando no hay material). */
  productLabel(product: string): string {
    const opt = this.productOptions.find((p) => p.value === product);
    return opt ? opt.label : (product || '—');
  }

  /** Solo lo aún no respondido por MOBICO admite acciones (pedidos y cambios). */
  isActionable(row: any): boolean {
    return ['pending', 'sent', 'review'].includes(row.status);
  }

  /** Cambios aún sin respuesta de MOBICO (definen qué muestra la fila del pedido). */
  private isChangeInFlight(c: any): boolean {
    return ['pending', 'sent', 'review'].includes(c.status);
  }

  /**
   * Estatus visible de una solicitud de cambio con el tipo integrado, para que el
   * historial se lea directo (ej. "Cambio aprobado", "Cancelación rechazada").
   */
  private changeStatusDisplay(kind: string, c: any): string {
    // REGLA DESACTIVADA (2026-07-06) — estatus "En espera de disponibilidad" del
    // review por la regla de 48 hrs (nunca aplicaba a cancelaciones):
    // if (kind !== 'cancel' && c.status === 'review' && (c.week_status || '').includes('espera de disponibilidad')) {
    //   return 'En espera de disponibilidad';
    // }
    const subject = kind === 'cancel' ? 'Cancelación' : (kind === 'addition' ? 'Adición' : 'Cambio');
    const fem = kind === 'cancel' || kind === 'addition';
    const byStatus: { [key: string]: string } = {
      pending: `${subject} pendiente`,
      sent: `${subject} pendiente`,
      review: `${subject} en revisión`,
      confirmed: fem ? `${subject} aprobada` : `${subject} aprobado`,
      rejected: fem ? `${subject} rechazada` : `${subject} rechazado`,
    };
    return byStatus[c.status] || this.changeStatusLabels[c.status] || c.status;
  }

  /**
   * Historial de cambios de un pedido como filas hijas: SOLO los cambios ya RESUELTOS
   * por MOBICO (aprobados/rechazados), más reciente arriba. El cambio en vuelo no se
   * lista: ya lo muestra la fila padre (listarlo también lo duplicaba). Hereda el
   * contexto del pedido (PO/Estación/Capacidad/Producto/Fecha requerida) para
   * renderizarse con todas las columnas de la tabla. El pedido de origen no se lista
   * (ya es la fila padre).
   */
  buildHistoryFor(order: any): any[] {
    const base = {
      is_history: true,
      po_number: order.po_number,
      station_name: order.station_name,
      truck_capacity: order.truck_capacity,
      material: order.material,
      order_delivery_date: order.order_delivery_date,
    };
    return (this.changesRows || [])
      .filter((c: any) => c.order_id === String(order._id) && !this.isChangeInFlight(c))
      .map((c: any) => ({ ...c, ...base }))
      .sort((a: any, b: any) => b._ts - a._ts);
  }

  /**
   * Reconstruye la tabla: una fila por pedido (el cambio en vuelo si lo hay, si no el
   * pedido), ordenada por fecha de solicitud descendente; si el pedido está expandido,
   * sus filas de historial se insertan justo debajo ocupando las mismas columnas.
   */
  private rebuildRequests(): void {
    // Última actividad por pedido: el timestamp más reciente entre sus cambios
    // (updated_at = cuándo respondió MOBICO; created_at si sigue en vuelo). Clave
    // del ORDEN de la tabla: lo recién movido —solicitado, aprobado o rechazado—
    // sube, en vez de que la fila regrese a su posición de creación al resolverse.
    const activityByOrder = new Map<string, number>();
    (this.changesRows || []).forEach((c: any) => {
      if (!c.order_id) { return; }
      const ts = new Date(c.updated_at || c.request_date).getTime() || 0;
      activityByOrder.set(c.order_id, Math.max(activityByOrder.get(c.order_id) || 0, ts));
    });

    const parents: any[] = [];
    (this.orderRows || []).forEach((r: any) => {
      const history = this.buildHistoryFor(r);
      const hasHistory = history.length > 0;
      const inflight = this.inFlightByOrder.get(String(r._id));
      const parentRow = inflight
        ? { ...inflight, _order_id: String(r._id), has_history: hasHistory, _history: history }
        : { ...r, request_date: r.date, change_kind: 'addition', type_label: this.changeTypeLabels['addition'], new_value: '—', _order_id: String(r._id), has_history: hasHistory, _history: history };
      // Actividad del pedido: lo más reciente entre el propio pedido (updated_at
      // cubre creación, edición y respuesta de MOBICO) y sus cambios.
      parentRow._activity_ts = Math.max(
        new Date(r.updated_at || r.date).getTime() || 0,
        activityByOrder.get(String(r._id)) || 0
      );
      parents.push(parentRow);
    });
    // Lo movido más recientemente arriba (creación o respuesta de MOBICO).
    parents.sort((a: any, b: any) => b._activity_ts - a._activity_ts);

    // Lista COMPLETA de pedidos: la usa el aviso de solicitudes nuevas, que no
    // debe depender de lo ya pintado.
    this.allParents = parents;

    // Corte de render sobre los PEDIDOS ya ordenados; el historial expandido se
    // inserta después, dentro del mismo corte.
    const visibleParents = parents.slice(0, this.renderLimit);

    const result: any[] = [];
    visibleParents.forEach((p: any) => {
      result.push(p);
      if (p.has_history && this.expandedHistory.has(p._order_id)) {
        p._history.forEach((ev: any) => result.push(ev));
      }
    });
    // Relleno con filas vacías hasta minRows (espejo de la vista del solicitante):
    // la tabla conserva su altura fija aunque haya pocos pedidos, sin hueco bajo
    // el último. Solo si hay ≥1 fila real (con 0 se deja vacío para que el
    // emptyMessage se muestre). _placeholder marca la fila para ocultar su
    // contenido vía CSS (ver getRowClass + .pipes-empty-row); el _id único
    // mantiene trackByProp/rowIdentity estables.
    if (result.length > 0) {
      for (let i = result.length; i < this.minRows; i++) {
        result.push({ _id: `__empty_${i}`, _placeholder: true });
      }
    }
    this.requests = result;
  }

  /**
   * UN solo recálculo de anchos tras la carga, endurecido (fix 2026-07-10 v2,
   * "columnas corridas / header y body desincronizados"): la 1.ª iteración
   * llamaba recalculate() en cada rebuild vía setTimeout, y se comprobó que el
   * recálculo mismo era quien CORROMPÍA el reparto — corría en frames donde el
   * contenedor aún no tenía su ancho final (la tabla vive tras *ngIf) y con
   * innerWidth 0 el modo force aplasta las columnas flexibles a 0px, estado
   * PEGAJOSO (el reparto es proporcional al ancho actual: una columna en 0 ya
   * no recibe nada, ni con resize). Defensas actuales: (1) pisos [minWidth] en
   * TODAS las columnas del template — el clamp impide el colapso a 0 aunque un
   * cálculo corra con ancho malo; (2) este recálculo se dispara SOLO tras
   * terminar la carga, con doble requestAnimationFrame (layout ya estabilizado,
   * no un timer ciego) y guard de ancho > 0.
   */
  private scheduleTableRecalc(): void {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const el = this.requestsTable?.element as HTMLElement | undefined;
      if (el && el.clientWidth > 0) {
        this.requestsTable?.recalculate();
        // Refresco de FILAS tras corregir el modelo de columnas (fix v3): con
        // fixed-row + virtualización, ngx-datatable reutiliza los nodos de fila
        // y algunas celdas del body conservaban el style.width fraccional del
        // cálculo anterior (p. ej. Producto 105.4px vs los 90px ya corregidos
        // del header) hasta que un hover forzaba su re-render — ese salto era
        // el corrimiento restante. Se clonan array Y objetos de fila: con
        // [trackByProp]="'_id'" + filas OnPush, la sola referencia nueva del
        // array no marca dirty a las filas reutilizadas (mismo objeto = mismo
        // input); el clon superficial sí, y el trackBy por _id conserva el DOM
        // (sin re-animaciones del historial). Repinta todas las celdas con los
        // anchos ya corregidos.
        this.requests = this.requests.map((r: any) => ({ ...r }));
      }
    }));
  }

  /** Scroll del contenedor de la tabla: al acercarse al fondo (~4 filas) se
   *  pinta el siguiente lote de pedidos. Scroll infinito client-side: los datos
   *  ya están cargados, solo se difiere el render. */
  onOrdersScroll(event: Event): void {
    if (this.renderLimit >= this.allParents.length) { return; }
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 128) {
      this.renderLimit += this.renderBatch;
      this.rebuildRequests();
    }
  }

  /**
   * Alterna el desglose de historial de un pedido (inserta/quita sus filas hijas).
   * El cierre es en DOS tiempos para que haya animación de salida (ngx-datatable
   * no tiene exit hook): primero las filas hijas se marcan `_closing` (corre la
   * animación oh-conceal y el chevron regresa), y al terminar se retiran del array.
   */
  toggleHistory(row: any): void {
    const id = row._order_id;
    if (this.expandedHistory.has(id)) {
      if (this.closingHistory.has(id)) { return; }
      this.closingHistory.add(id);
      this.requests = this.requests.map((r: any) =>
        (r.is_history && r.order_id === id) ? { ...r, _closing: true } : r);
      setTimeout(() => {
        this.closingHistory.delete(id);
        this.expandedHistory.delete(id);
        this.rebuildRequests();
      }, 160);
      return;
    }
    this.expandedHistory.add(id);
    this.rebuildRequests();
  }

  /** true si el desglose de historial del pedido está abierto (y no cerrándose). */
  isHistoryOpen(row: any): boolean {
    return this.expandedHistory.has(row._order_id) && !this.closingHistory.has(row._order_id);
  }

  /** Marca las filas hijas del historial, las que están saliendo y las de relleno
   *  (placeholder "—") para el CSS. */
  getRowClass = (row: any) => ({
    'history-subrow': !!row?.is_history,
    'history-closing': !!row?._closing,
    'pipes-empty-row': !!row?._placeholder,
  });

  /** Estaciones para el filtro buscable: si hay zona activa, solo las de esa zona. */
  getStationsGroup() {
    this.showStations = false;
    this.gasprecioService.getStationsGroupZone(this.idGroup, this.idZone || '').subscribe((data: any) => {
      const stations: any = data || [];
      this.stationOptions = [
        { label: 'Todas las estaciones', value: '' },
        ...stations.map((s: any) => ({ label: s.alias, value: s._id })),
      ];
      this.showStations = true;
    });
  }

  /** @param silent true = recarga interna sin loader (refresh automático). */
  getData(silent = false) {
    if (!silent) { this.reqst = 0; }
    // Toda recarga arranca con los historiales COLAPSADOS (pedido del equipo:
    // al volver a entrar o refrescar, el historial oculto para no perderse);
    // la expansión es un gesto puntual de consulta, no un estado que persista.
    this.expandedHistory.clear();
    this.closingHistory.clear();
    // Toda recarga (entrar, filtros, refresh) regresa al inicio del listado.
    this.renderLimit = this.renderBatch;
    this.loading = true;
    this.idZone ??= '';
    if (this.startDate === undefined || this.endDate === undefined) {
      this.startDate = moment().startOf('isoWeek').toISOString();
      this.endDate = moment(this.startDate).add(6, 'days').toISOString();
    }

    forkJoin([
      this.gasprecioService.getListPurchases(this.idGroup, this.startDate, this.endDate, this.idZone, 'orders', this.stationFilter),
      this.fuelOrders.getChanges({ group: this.idGroup, zones: this.idZone, week: this.startDate, station: this.stationFilter })
    ]).subscribe({
      next: ([orders, changesData]: any) => {
        const rows = (orders || []).map((o: any) => ({ ...o, material: o.material || this.productLabel(o.product) }));

        const changes = changesData?.changes || {};
        // Valor nuevo solicitado, ya formateado para la columna "Nuevo valor".
        const newValueOf = (type: string, c: any): string => {
          if (type === 'date' && c.new_delivery_date) { return moment.utc(c.new_delivery_date).format('DD/MM/YY'); }
          if (type === 'capacity' && c.new_truck_capacity) { return new Intl.NumberFormat('es-MX').format(c.new_truck_capacity); }
          if (type === 'product') {
            const opt = this.productOptions.find((p) => p.value === c.new_product);
            return c.new_material || (opt ? opt.label : c.new_product) || '—';
          }
          return '—';
        };
        const normalize = (type: string, c: any) => ({
          ...c,
          request_date: c.created_at, // cuándo se creó la solicitud de cambio
          change_kind: type,
          type_label: this.changeTypeLabels[type],
          station_name: c.station,
          order_delivery_date: c.delivery_date,
          material: c.material || this.productLabel(c.product),
          new_value: newValueOf(type, c),
          statusName: this.changeStatusDisplay(type, c),
        });

        // Cambio EN VUELO por pedido (a lo más uno: el backend rechaza un segundo);
        // define lo que muestra la fila del pedido (Aprobar/Rechazar responden ese cambio).
        const inFlightByOrder = new Map<string, any>();
        // Historial: TODOS los cambios (cualquier estatus) aplanados por pedido, para el
        // desglose expandible. Cada evento hereda luego el contexto del pedido para
        // renderizarse con todas las columnas de la tabla.
        const allChanges: any[] = [];
        ['addition', 'cancel', 'date', 'capacity', 'product'].forEach((type) => {
          (changes[type] || []).forEach((c: any) => {
            if (!c.order_id) { return; }
            if (this.isChangeInFlight(c)) {
              inFlightByOrder.set(String(c.order_id), { ...normalize(type, c), in_flight_change: true });
            }
            allChanges.push({
              is_history: true,
              // Identidad estable de la fila (doc de cambio): la usa rowIdentity para
              // que ngx-datatable reutilice el DOM y no re-anime filas ya visibles.
              _id: c._id,
              order_id: String(c.order_id),
              change_kind: type,
              type_label: this.changeTypeLabels[type],
              request_date: c.created_at,
              new_value: newValueOf(type, c),
              status: c.status,
              statusName: this.changeStatusDisplay(type, c),
              week_status: c.week_status,
              // updated_at = cuándo respondió MOBICO (resueltos): alimenta el
              // orden por última actividad de la tabla.
              updated_at: c.updated_at,
              _ts: new Date(c.created_at).getTime(),
            });
          });
        });

        this.orderRows = rows;
        this.inFlightByOrder = inFlightByOrder;
        this.changesRows = allChanges;
        this.rebuildRequests();

        this.maybeShowNewRequestNotices();
      },
      complete: () => {
        this.reqst = 1;
        this.loading = false;
        // reqst=1 recién renderiza la tabla (*ngIf): un solo recálculo de
        // anchos con el layout ya estable (ver scheduleTableRecalc).
        this.scheduleTableRecalc();
      },
      error: (err) => {
        console.error(err);
        this.reqst = 1;
        this.loading = false;
        this.scheduleTableRecalc();
      }
    });
  }

  /**
   * Aviso único al admin de las solicitudes NUEVAS por revisar (pedidos, cambios y
   * cancelaciones): un toast breve con el conteo (el detalle vive en la tabla). Los
   * ids ya avisados se guardan en localStorage; la primera vez solo se registra lo
   * existente, sin aviso.
   */
  private maybeShowNewRequestNotices(): void {
    // Solo lo aún por responder genera aviso (la tabla también muestra lo resuelto).
    // Sobre TODOS los pedidos, no solo la página visible (this.requests pagina).
    const pending = this.allParents.filter((r: any) => this.isActionable(r));
    const allIds = pending.map((x: any) => String(x._id));

    let seen: any = null;
    try { seen = JSON.parse(localStorage.getItem(this.noticeStorageKey)); } catch { seen = null; }
    if (!Array.isArray(seen)) {
      localStorage.setItem(this.noticeStorageKey, JSON.stringify(allIds));
      return;
    }

    const seenSet = new Set(seen.map(String));
    const news = pending.filter((x: any) => !seenSet.has(String(x._id)));
    if (!news.length) { return; }
    // Si hay un Swal abierto (otro toast o un diálogo del legacy), reintenta en el
    // siguiente refresh: Swal es de instancia única y dispararía el cierre del actual.
    if (Swal.isVisible()) { return; }

    // Se marcan como avisados ANTES de mostrar: el aviso es una sola vez.
    localStorage.setItem(this.noticeStorageKey, JSON.stringify([...seen.map(String), ...news.map((x: any) => String(x._id))]));

    // Desglose por tipo en CHIPS (task 2026-07-10 "organizar alertas": el renglón
    // corrido "Producto (2) · Capacidad (2) · ..." se partía en líneas ilegibles
    // con muchos tipos). Cada tipo sale como chip soft-tint con contador — los
    // MISMOS colores que la columna Tipo de la tabla (.type-chip), así el aviso
    // se escanea igual que el triage. Orden: por volumen descendente (lo más
    // cargado primero). Con una sola solicitud, su chip + PO y estación.
    // Estilos en styles.scss bajo .pipes-toast (el toast vive en <body>).
    let detail: string;
    if (news.length === 1) {
      const r = news[0];
      detail =
        `<div class="pipes-toast__chips">${this.typeChipHtml(r.change_kind, r.type_label)}</div>` +
        `<div class="pipes-toast__note">PO ${this.esc(r.po_number || '—')} · ${this.esc(r.station_name || '')}</div>`;
    } else {
      const byKind = new Map<string, { label: string; count: number }>();
      news.forEach((r: any) => {
        const entry = byKind.get(r.change_kind) || { label: r.type_label, count: 0 };
        entry.count += 1;
        byKind.set(r.change_kind, entry);
      });
      const chips = [...byKind.entries()]
        .sort((a, b) => b[1].count - a[1].count)
        .map(([kind, e]) => this.typeChipHtml(kind, e.label, e.count))
        .join('');
      detail =
        `<div class="pipes-toast__chips">${chips}</div>` +
        `<div class="pipes-toast__note">El detalle está en la tabla.</div>`;
    }
    const title = news.length === 1
      ? 'Solicitud nueva por revisar'
      : `${news.length} solicitudes nuevas por revisar`;
    this.showToast('info', title, detail);
  }

  /** Chip soft-tint de tipo para el html del toast. La clase de color se toma de
   *  una whitelist (change_kind viene de nuestro propio mapeo, pero el html del
   *  toast se inyecta con innerHTML: nada dinámico sin filtrar). */
  private typeChipHtml(kind: string, label: string, count?: number): string {
    const safeKind = ['addition', 'cancel', 'date', 'capacity', 'product'].includes(kind) ? kind : '';
    const suffix = count && count > 1 ? ` <b>×${count}</b>` : '';
    return `<span class="pipes-toast__chip ${safeKind ? `pipes-toast__chip--${safeKind}` : ''}">${this.esc(label)}${suffix}</span>`;
  }

  /** Toast del flujo: aviso breve no bloqueante que se cierra solo. Con texto de
   *  detalle se fija un ancho cómodo para que la línea no se parta en pedazos.
   *  Look moderno del flujo vía customClass .pipes-toast (styles.scss, espejo
   *  del showToast del solicitante): card blanca radio 12 + ícono FA en círculo
   *  soft-tint en lugar del aro animado default de Swal. */
  private showToast(icon: 'success' | 'error' | 'info', title: string, html?: string): void {
    const iconHtml = {
      success: '<i class="fa fa-check" aria-hidden="true"></i>',
      error: '<i class="fa fa-times" aria-hidden="true"></i>',
      info: '<i class="fa fa-info" aria-hidden="true"></i>',
    }[icon];
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon,
      iconHtml,
      title,
      html,
      // 490 acompaña la escala grande del look .pipes-toast (espejo del
      // showToast del solicitante).
      width: html ? 490 : undefined,
      showConfirmButton: false,
      showCloseButton: true,
      timer: 7000,
      timerProgressBar: true,
      customClass: { popup: `pipes-toast pipes-toast--${icon}` },
    });
  }

  /** Escapa valores de datos para el html del toast. */
  private esc(v: any): string {
    return String(v ?? '').replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' }[c]));
  }

  /**
   * Aprueba un pedido o una solicitud de cambio (status: confirmed). La respuesta al
   * solicitante es el propio estatus (sin texto libre). Si es un cambio, el backend
   * lo aplica al pedido original y CONFIRMA el pedido completo (aunque siguiera
   * pendiente): aprobar el cambio es aprobar la solicitud.
   */
  approveOrder(row: any): void {
    this.confirmError = '';
    // Fecha de entrega: se captura al aprobar pedidos primarios Y cambios (aprobar
    // el cambio confirma el pedido completo, así que también se fecha). Solo la
    // cancelación no la pide: aprobarla cancela el pedido, no hay entrega.
    // Prefill en UTC: las fechas del back son medianoche UTC y moment() local las
    // corría un día en MX. Para cambios de fecha, order_delivery_date ya es la nueva.
    const needsDeliveryDate = !(row.in_flight_change && row.change_kind === 'cancel');
    this.confirmDeliveryDate = needsDeliveryDate && row.order_delivery_date
      ? moment.utc(row.order_delivery_date).format('YYYY-MM-DD')
      : '';
    // La entrega no puede ser anterior a HOY: si la fecha solicitada ya pasó
    // (aprobación tardía), el prefill se ajusta al día corriente para no abrir
    // el modal con el botón Aprobar bloqueado sin motivo visible.
    if (this.confirmDeliveryDate && this.confirmDeliveryDate < this.minDeliveryDate) {
      this.confirmDeliveryDate = this.minDeliveryDate;
    }
    // Litros aceptados: se precargan con los efectivos tras el cambio a aprobar (el
    // admin los ajusta a lo que Mobico realmente acepta); costos vacíos. Solo aplica
    // cuando hay entrega (no en cancelaciones).
    this.confirmLiters = needsDeliveryDate ? this.defaultAcceptedLiters(row) : null;
    this.confirmTotal = null;
    this.confirmSupplyPrice = null;
    this.lastCostEdited = null;
    this.confirmAction = { row, status: 'confirmed', title: 'Aprobar solicitud', actionLabel: 'Aprobar', successTitle: 'Solicitud aprobada', needsDeliveryDate };
  }

  /** Rechaza un pedido o una solicitud de cambio (status: rejected). */
  rejectOrder(row: any): void {
    this.confirmError = '';
    this.confirmDeliveryDate = '';
    this.resetDeliveryCapture();
    this.confirmAction = { row, status: 'rejected', title: 'Rechazar solicitud', actionLabel: 'Rechazar', successTitle: 'Solicitud rechazada', needsDeliveryDate: false };
  }

  /** Cierra el modal de confirmación sin responder. */
  cancelConfirmAction(): void {
    if (this.respondingOrder) { return; }
    this.confirmAction = null;
    this.confirmError = '';
    this.confirmDeliveryDate = '';
    this.showDeliveryCalendar = false;
    this.resetDeliveryCapture();
  }

  /** Limpia los campos de entrega/costos capturados al aprobar. */
  private resetDeliveryCapture(): void {
    this.confirmLiters = null;
    this.confirmTotal = null;
    this.confirmSupplyPrice = null;
    this.lastCostEdited = null;
  }

  // ---- Mini calendario de "Fecha de entrega" (task 2026-07-13 "igual que el
  // selector del cambio de fecha"; réplica scoped del op-mini-cal de
  // pipes.component — patrón de duplicación tipo type-chip). La fecha SOLICITADA
  // por la estación se marca en gris como referencia (SÍ seleccionable: entregar
  // el día pedido es válido, a diferencia del cambio de fecha donde elegir la
  // actual no cambiaría nada) y la fecha de entrega elegida va en verde. ----
  showDeliveryCalendar = false;
  // Posición del panel flotante (capa fija): anclado al campo por su borde
  // IZQUIERDO — el campo es el primero del renglón del modal, al revés del
  // modal de cambio que ancla por la derecha.
  calPanelPos: { top: string; left: string } | null = null;

  toggleDeliveryCalendar(ev: MouseEvent): void {
    this.showDeliveryCalendar = !this.showDeliveryCalendar;
    if (!this.showDeliveryCalendar) { return; }
    const r = (ev.target as HTMLElement).getBoundingClientRect();
    // Altura real del picker ≈ 320px (6 filas + weekdays + header + leyenda).
    const panelH = 320;
    const panelW = 300;
    const gap = 6;
    let top = r.bottom + gap; // preferencia: SIEMPRE abajo del campo
    if (top + panelH > window.innerHeight - 12) {
      top = Math.max(12, r.top - gap - panelH);
    }
    const left = Math.min(Math.max(16, r.left), window.innerWidth - panelW - 16);
    this.calPanelPos = { top: `${top}px`, left: `${left}px` };
  }

  onCalSelect(d: { year: number; month: number; day: number }): void {
    this.confirmDeliveryDate = this.calKey(d);
    this.showDeliveryCalendar = false;
  }

  /** Mes inicial: el de la fecha ya elegida (prefill = la solicitada). */
  get calStartDate(): { year: number; month: number } | null {
    if (!this.confirmDeliveryDate) { return null; }
    const m = moment(this.confirmDeliveryDate, 'YYYY-MM-DD');
    return { year: m.year(), month: m.month() + 1 };
  }

  /** Hoy (MX) como tope inferior: no se aprueba una entrega en el pasado. */
  get calMinDate(): { year: number; month: number; day: number } {
    const t = moment.tz('America/Mexico_City');
    return { year: t.year(), month: t.month() + 1, day: t.date() };
  }

  private calKey(d: { year: number; month: number; day: number }): string {
    return `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;
  }

  /** Día de la fecha REQUERIDA por la estación (gris de referencia). */
  isCalRequested(d: { year: number; month: number; day: number }): boolean {
    const cur = this.confirmAction?.row?.order_delivery_date;
    return !!cur && moment.utc(cur).format('YYYY-MM-DD') === this.calKey(d);
  }

  /** Día elegido como fecha de entrega (relleno verde). */
  isCalSelected(d: { year: number; month: number; day: number }): boolean {
    return !!this.confirmDeliveryDate && this.confirmDeliveryDate === this.calKey(d);
  }

  /** Litros válidos para multiplicar/dividir: finito y > 0 (evita /0 y NaN). */
  private get litersForCalc(): number | null {
    const l = this.confirmLiters;
    return l != null && Number.isFinite(l) && l > 0 ? l : null;
  }

  /** Redondeo seguro a N decimales. */
  private roundTo(value: number, decimals: number): number {
    const f = Math.pow(10, decimals);
    return Math.round((value + Number.EPSILON) * f) / f;
  }

  /**
   * Costo por litro (P) y costo total (T) están ligados por T = L × P. Al escribir
   * uno, se deriva el otro a partir de los litros aceptados (L). Se deriva de forma
   * programática (no re-dispara ngModelChange → sin bucles). Si falta L (vacío o 0)
   * o el valor escrito no es válido, no se toca el otro campo.
   */
  onSupplyPriceChange(): void {
    this.lastCostEdited = 'supply';
    const liters = this.litersForCalc;
    const supply = this.confirmSupplyPrice;
    if (liters != null && supply != null && Number.isFinite(supply) && supply >= 0) {
      // Total en pesos ENTEROS (formato del equipo 2026-07-13: sin decimales).
      this.confirmTotal = this.roundTo(liters * supply, 0);
    }
  }

  /** Admin editó el costo total → deriva el costo por litro (si hay litros > 0). */
  onTotalChange(): void {
    this.lastCostEdited = 'total';
    const liters = this.litersForCalc;
    const total = this.confirmTotal;
    if (liters != null && total != null && Number.isFinite(total) && total >= 0) {
      // 2 decimales: lo que se muestra ("21.70") es lo que se guarda — antes se
      // derivaba a 4 y el modelo cargaba decimales invisibles para el admin.
      this.confirmSupplyPrice = this.roundTo(total / liters, 2);
    }
  }

  /**
   * Cambiaron los litros aceptados → recalcula el costo DERIVADO según cuál escribió
   * el admin por última vez (el que él capturó se mantiene; el otro se recalcula).
   */
  onLitersChange(): void {
    const liters = this.litersForCalc;
    if (liters == null) {
      // Sin litros no se sostiene el costo DERIVADO (T = L × P): se limpia el
      // calculado y se conserva el que capturó el admin (precio/litro o total valen
      // por sí solos). Al volver a poner litros se recalcula el derivado.
      if (this.lastCostEdited === 'supply') { this.confirmTotal = null; }
      else if (this.lastCostEdited === 'total') { this.confirmSupplyPrice = null; }
      return;
    }
    if (this.lastCostEdited === 'supply' && this.confirmSupplyPrice != null && Number.isFinite(this.confirmSupplyPrice)) {
      this.confirmTotal = this.roundTo(liters * this.confirmSupplyPrice, 0);
    } else if (this.lastCostEdited === 'total' && this.confirmTotal != null && Number.isFinite(this.confirmTotal)) {
      this.confirmSupplyPrice = this.roundTo(this.confirmTotal / liters, 2);
    }
  }

  /**
   * Litros a precargar en el modal de aprobación: el valor efectivo tras aplicar el
   * cambio que se aprueba. En un cambio de capacidad en vuelo el nuevo valor viene en
   * new_truck_capacity (el resto de campos de la fila son el "antes"); en los demás
   * casos (otros cambios o pedido primario) liters_requested ya es el vigente.
   */
  private defaultAcceptedLiters(row: any): number | null {
    if (row.in_flight_change && row.change_kind === 'capacity') {
      return row.new_truck_capacity ?? row.liters_requested ?? null;
    }
    return row.liters_requested ?? null;
  }

  /**
   * Valor a mostrar en la columna Capacidad: si Mobico ya definió los litros
   * aceptados/entregados (liters), se muestra ese valor; si no, la capacidad
   * solicitada (truck_capacity).
   */
  capacityValue(row: any): number | null {
    return row?.liters != null ? row.liters : (row?.truck_capacity ?? null);
  }

  /**
   * Tooltip de la columna Capacidad. Con litros aceptados detalla lo solicitado, lo
   * aceptado por Mobico y la diferencia entre ambos; si aún no hay aceptado, indica
   * que es la capacidad solicitada.
   */
  capacityTitle(row: any): string {
    if (row?.liters == null) { return 'Capacidad solicitada'; }
    const fmt = (n: number) => new Intl.NumberFormat('es-MX').format(n);
    const requested = row.liters_requested ?? row.truck_capacity ?? null;
    const accepted = row.liters;
    if (requested == null) { return `Aceptado por Mobico: ${fmt(accepted)} L`; }
    const diff = accepted - requested;
    const diffText = diff === 0 ? 'sin diferencia' : `${diff > 0 ? '+' : '-'}${fmt(Math.abs(diff))} L`;
    // Saltos de línea: el tooltip (.cap-tooltip) los respeta vía white-space: pre-line
    return `Solicitado: ${fmt(requested)} L\nAceptado: ${fmt(accepted)} L\nDiferencia: ${diffText}`;
  }

  /**
   * Muestra el icono de info en Capacidad solo cuando el pedido está confirmado y
   * los litros aceptados difieren de lo solicitado (Mobico entregó una cantidad
   * distinta a la pedida).
   */
  showCapacityDiff(row: any): boolean {
    const requested = row?.liters_requested ?? row?.truck_capacity ?? null;
    return row?.status === 'confirmed' && row?.liters != null && requested != null && row.liters !== requested;
  }

  /**
   * Piso de la fecha de entrega al aprobar: el día corriente (feedback 2026-07-10
   * "no se debe poder aprobar con una fecha anterior"). Getter para que siga
   * siendo válido si el modal queda abierto cruzando la medianoche.
   */
  get minDeliveryDate(): string {
    return moment().format('YYYY-MM-DD');
  }

  /** true si la fecha capturada existe pero es anterior a hoy (bloquea Aprobar). */
  get deliveryDateTooEarly(): boolean {
    // Comparación lexicográfica: válida en formato YYYY-MM-DD.
    return !!this.confirmDeliveryDate && this.confirmDeliveryDate < this.minDeliveryDate;
  }

  /** Al aprobar, los datos de entrega (fecha, litros, costos) son OPCIONALES
   *  (feedback 2026-07-13: el admin no está obligado a capturarlos; revierte
   *  la obligatoriedad del fix 2026-07-10). Lo único que bloquea Aprobar es
   *  una fecha de entrega capturada anterior a hoy. */
  get confirmActionValid(): boolean {
    const action = this.confirmAction;
    if (!action) { return false; }
    if (!action.needsDeliveryDate) { return true; }
    return !this.deliveryDateTooEarly;
  }

  /**
   * Envía la respuesta (aprobar/rechazar). El botón muestra el avance (evita dobles
   * clicks); si el API falla, el error queda inline en el propio modal.
   */
  async confirmRespond(): Promise<void> {
    const action = this.confirmAction;
    if (!action || this.respondingOrder || !this.confirmActionValid) { return; }
    this.respondingOrder = true;
    this.confirmError = '';
    try {
      const body: { status: string; delivery_date?: string; liters?: number; total?: number; supply_price?: number } = { status: action.status };
      if (action.needsDeliveryDate) {
        // Todos los datos de entrega son opcionales: solo viajan los capturados.
        if (this.confirmDeliveryDate) { body.delivery_date = this.confirmDeliveryDate; }
        if (this.confirmLiters != null) { body.liters = this.confirmLiters; }
        if (this.confirmTotal != null) { body.total = this.confirmTotal; }
        if (this.confirmSupplyPrice != null) { body.supply_price = this.confirmSupplyPrice; }
      }
      // Detalle del toast de éxito (mismo lenguaje que el aviso de solicitudes
      // nuevas: chip de tipo + PO/estación); se arma ANTES de limpiar el estado
      // del modal porque la fecha/litros capturados viven ahí. Al aprobar con
      // entrega se anexa lo pactado para que el aviso confirme lo que se guardó.
      const row = action.row;
      let toastDetail =
        `<div class="pipes-toast__chips">${this.typeChipHtml(row.change_kind, row.type_label)}</div>` +
        `<div class="pipes-toast__note">PO ${this.esc(row.po_number || '—')} · ${this.esc(row.station_name || '')}</div>`;
      if (action.status === 'confirmed' && action.needsDeliveryDate && this.confirmDeliveryDate) {
        const parts = [`Entrega ${moment(this.confirmDeliveryDate).format('DD/MM/YYYY')}`];
        if (this.confirmLiters != null) {
          parts.push(`${new Intl.NumberFormat('es-MX').format(this.confirmLiters)} lts.`);
        }
        toastDetail += `<div class="pipes-toast__note">${parts.join(' · ')}</div>`;
      }
      await firstValueFrom(this.fuelOrders.updateOrderStatus(action.row._id, body));
      this.confirmAction = null;
      this.confirmDeliveryDate = '';
      this.showDeliveryCalendar = false;
      this.resetDeliveryCapture();
      this.getData();
      this.showToast('success', action.successTitle, toastDetail);
    } catch (err: any) {
      console.error('Error respondiendo solicitud', err);
      this.confirmError = err?.error?.err || 'No se pudo guardar, intenta de nuevo.';
    } finally {
      this.respondingOrder = false;
    }
  }

}
